import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ConfirmDialog, Toggle } from '../../components';
import { GrantAccessModal } from './grant-access-modal';
import type { WideVGAccessOperation } from './wide-access';
import { useOperationLocks, useSetWideVGAccess, WIDE_VG_SERVICE, wideVGProcess } from './wide-access';

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 13px;
  min-height: 34px;
  font-size: 15px;
  font-weight: 700;
  color: var(--white);
`;

const Card = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: rgba(17, 27, 51, 0.3);
  padding: 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IconBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 11px;
  display: inline-grid;
  place-items: center;
  font-size: 18px;
`;

const Title = styled.div`
  margin: 0 0 7px;
  font-weight: 700;
  font-size: 15px;
  color: rgb(240, 242, 245);
`;

const Text = styled.p`
  margin: 0 0 18px;
  max-width: 420px;
  font-weight: 500;
  font-size: 12px;
  color: rgba(176, 186, 209, 0.72);
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 9px;
  font-weight: 600;
  font-size: 13px;
  padding: 11px 18px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover,
  &:focus {
    outline: none;
    opacity: 0.92;
  }
`;

const DangerBox = styled.div`
  border: 1px solid rgba(255, 80, 95, 0.3);
  border-radius: 12px;
  background: rgba(255, 80, 95, 0.06);
  padding: 17px 20px;
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;

  .info {
    flex: 1 1 0%;
    min-width: 220px;
  }

  .info-title {
    font-weight: 700;
    font-size: 14px;
    color: rgb(240, 242, 245);
  }

  .info-text {
    margin-top: 4px;
    font-weight: 500;
    font-size: 12px;
    color: rgba(176, 186, 209, 0.68);
    line-height: 1.55;
  }

  .status {
    margin-top: 8px;
    font-weight: 600;
    font-size: 12px;
    color: rgba(213, 221, 238, 0.92);

    i {
      margin-right: 6px;
    }

    &.error {
      color: rgb(255, 138, 147);
    }
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 11px;
    flex-shrink: 0;
  }
`;

// Kept as its own const so the apostrophe doesn't trip react/no-unescaped-entities.
const DESCRIPTION =
  "Assigned crew is managed in the marketplace. From here you can grant an additional videographer manual access to this event's encoder. Access can't be listed or revoked from this panel.";

interface ConfirmCopy {
  title: string;
  message: string;
  confirmLabel: string;
}

// Two-step confirmation copy per operation (step 1 = intent, step 2 = final check).
const CONFIRM_COPY: Record<WideVGAccessOperation, Record<1 | 2, ConfirmCopy>> = {
  grant: {
    1: {
      title: 'Enable fleet-wide access',
      message:
        'This dispatches an access script that grants every videographer manual access to every encoder in the fleet. Continue?',
      confirmLabel: 'Continue'
    },
    2: {
      title: 'Final confirmation',
      message: 'Last check: the script runs immediately across all encoders. Are you absolutely sure?',
      confirmLabel: 'Enable access'
    }
  },
  remove: {
    1: {
      title: 'Disable fleet-wide access',
      message:
        "This dispatches a script that removes every videographer's manual access to every encoder in the fleet. Continue?",
      confirmLabel: 'Continue'
    },
    2: {
      title: 'Final confirmation',
      message: 'Last check: the script runs immediately across all encoders. Are you absolutely sure?',
      confirmLabel: 'Disable access'
    }
  }
};

export const MarketplaceTab = (): ReactElement => {
  const [enabled, setEnabled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [confirm, setConfirm] = useState<{ operation: WideVGAccessOperation; step: 1 | 2 } | null>(
    null
  );

  const [statusMessage, setStatusMessage] = useState<string>();

  const [setWideAccess, { loading: mutating, error: mutationError }] = useSetWideVGAccess();
  const { locks, refetch: refetchLocks } = useOperationLocks(WIDE_VG_SERVICE);

  // A lock for our process means the grant/remove is currently running.
  const grantInProgress = locks.some((lock) => lock.process === wideVGProcess('grant'));
  const removeInProgress = locks.some((lock) => lock.process === wideVGProcess('remove'));

  // Reflect the server's in-flight process on the toggle when one is running.
  useEffect(() => {
    if (grantInProgress) {
      setEnabled(true);
    } else if (removeInProgress) {
      setEnabled(false);
    }
  }, [grantInProgress, removeInProgress]);

  const runOperation = useCallback(
    async (operation: WideVGAccessOperation): Promise<void> => {
      const response = await setWideAccess(operation);

      if (response) {
        setStatusMessage(response.message);

        if (response.started) {
          setEnabled(operation === 'grant');
        }
      }

      refetchLocks();
    },
    [setWideAccess, refetchLocks]
  );

  const handleToggle = (): void => {
    if (mutating) {
      return;
    }

    // Both directions require a two-step confirmation before firing.
    setConfirm({ operation: enabled ? 'remove' : 'grant', step: 1 });
  };

  const inProgressLabel = grantInProgress ? 'grant' : removeInProgress ? 'removal' : undefined;

  return (
    <div>
      <SectionTitle>Marketplace</SectionTitle>

      <Card>
        <IconBadge className='cc-blue' style={{ marginBottom: '16px' }}>
          <i className='far fa-user-plus' />
        </IconBadge>

        <Title>Grant encoder access</Title>
        <Text>{DESCRIPTION}</Text>

        <AddButton
          className='cc-primary'
          type='button'
          onClick={(): void => {
            setModalOpen(true);
          }}
        >
          <i className='far fa-plus-circle' /> Add videographer access
        </AddButton>
      </Card>

      <GrantAccessModal
        open={modalOpen}
        onClose={(): void => {
          setModalOpen(false);
        }}
      />

      {/* TODO: placeholder copy — replace title/text once provided. */}
      <DangerBox>
        <IconBadge className='cc-red'>
          <i className='fas fa-exclamation-triangle' />
        </IconBadge>

        <div className='info'>
          <div className='info-title'>Fleet-wide Encoder Access</div>
          <div className='info-text'>Dispatches a script granting every videographer manual access to every encoder.
            Double confirmation required. Can take a few minutes to reach every encoder, and can be disabled at any time.</div>

          {inProgressLabel !== undefined && (
            <div className='status'>
              <i className='fas fa-circle-notch fa-spin' />
              A fleet-wide {inProgressLabel} is in progress…
            </div>
          )}

          {mutationError ? (
            <div className='status error'>{mutationError.message}</div>
          ) : (
            inProgressLabel === undefined &&
            statusMessage !== undefined && <div className='status'>{statusMessage}</div>
          )}
        </div>

        <div className='controls'>
          <Toggle on={enabled} onChange={handleToggle} />
        </div>
      </DangerBox>

      {confirm && (
        <ConfirmDialog
          open
          tone='danger'
          title={CONFIRM_COPY[confirm.operation][confirm.step].title}
          message={CONFIRM_COPY[confirm.operation][confirm.step].message}
          confirmLabel={CONFIRM_COPY[confirm.operation][confirm.step].confirmLabel}
          onCancel={(): void => {
            setConfirm(null);
          }}
          onConfirm={(): void => {
            if (confirm.step === 1) {
              setConfirm({ operation: confirm.operation, step: 2 });
            } else {
              const { operation } = confirm;

              setConfirm(null);
              void runOperation(operation);
            }
          }}
        />
      )}
    </div>
  );
};
